import { v4 as uuid } from 'uuid';
import { PatientDataSet } from '../dicom/patientdataset';
import { DoseMatrix } from './doseMatrix';
import { Image3D } from './image3d';
import { Plan } from './plan';
import { RigidRegistration } from './rigidRegistration';
import { StructureSet } from './structureset';

/**
 * Domain object for a Patient. This class captures all the data related to a patient.
 */

export type ImageRegistration = {
    image: Image3D;
    registration: RigidRegistration | undefined;
};
export class Patient {
    /**
     * The DICOM data set for the patient
     */
    public dataSet: PatientDataSet;

    /**
     * Internal id\
     * Unique runtime identifier that is generated when the {@link Patient} is constructed.
     */
    public readonly id: string;

    private readonly _images: Image3D[] = new Array<Image3D>();

    private readonly _doses: DoseMatrix[] = new Array<DoseMatrix>();

    private readonly _structureSets: StructureSet[] = new Array<StructureSet>();

    private readonly _plans: Plan[] = new Array<Plan>();

    private readonly _registrations: RigidRegistration[] =
        new Array<RigidRegistration>();

    constructor(data: PatientDataSet, id: string = uuid()) {
        this.dataSet = data;
        this.id = id;
    }

    public addImage(image: Image3D) {
        this._images.push(image);
    }

    public get images(): ReadonlyArray<Image3D> {
        return this._images;
    }

    public getImageWithDicomUid(uid: string): Image3D | undefined {
        return this._images.find((value: Image3D): boolean => {
            return value.dicomUid === uid;
        });
    }

    public addDose(dose: DoseMatrix) {
        this._doses.push(dose);
    }

    public get doses(): ReadonlyArray<DoseMatrix> {
        return this._doses;
    }

    public getDoseWithDicomUid(uid: string): DoseMatrix | undefined {
        return this._doses.find((value: DoseMatrix): boolean => {
            return value.dicomUid === uid;
        });
    }

    public addStructureSet(structureSet: StructureSet) {
        this._structureSets.push(structureSet);
    }

    public get structureSets(): ReadonlyArray<StructureSet> {
        return this._structureSets;
    }

    public addPlan(plan: Plan) {
        this._plans.push(plan);
    }

    public get plans(): ReadonlyArray<Plan> {
        return this._plans;
    }

    public addRegistration(registration: RigidRegistration) {
        this._registrations.push(registration);
    }

    public get registrations(): ReadonlyArray<RigidRegistration> {
        return this._registrations;
    }

    public getBlendableImages(sourceimage: Image3D): ImageRegistration[] {
        const result = new Array<ImageRegistration>();
        for (const image of this._images) {
            for (const registration of this._registrations) {
                if (
                    registration.sourceFrameOfReferenceUid ===
                        sourceimage.frameOfReferenceUid &&
                    registration.targetFrameOfReferenceUid ===
                        image.frameOfReferenceUid
                ) {
                    result.push({ image: image, registration: registration });
                }
                if (
                    registration.targetFrameOfReferenceUid ===
                        sourceimage.frameOfReferenceUid &&
                    registration.sourceFrameOfReferenceUid ===
                        image.frameOfReferenceUid
                ) {
                    result.push({ image: image, registration: registration });
                }
            }
            if (
                sourceimage.frameOfReferenceUid === image.frameOfReferenceUid &&
                sourceimage !== image
            ) {
                result.push({ image: image, registration: undefined });
            }
        }
        return result;
    }

    public getStructureSetWithSopInstanceUid(
        uid: string
    ): StructureSet | undefined {
        return this._structureSets.find((value: StructureSet): boolean => {
            return value.dicomStructureSet.sopInstanceUID === uid;
        });
    }
}
